import { createSlice, createSelector } from '@reduxjs/toolkit';
import {
  updateProfile,
  updateProfileThunk,
} from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { transitions as purchaseTransitions } from '../transactions/transactionProcessPurchase';
import { CANONICAL_STATUSES, getCanonicalStatus } from '../util/transactionStatusMap';
import { PURCHASE_PROCESS_NAME, resolveLatestProcessName } from '../transactions/transaction';
import { types as sdkTypes } from '../util/sdkLoader';
import { trackAddedToCart } from '../util/klaviyo';
import { getListingsById } from './marketplaceData.duck';

const { UUID } = sdkTypes;

import {
  cartItemsArray,
  getCartFromLocalStorage,
  getCartItemCount,
  mergeCartMaps,
  normalizeCartItemsMap,
  bookmarksArrayToCartMap,
  cartMapToBookmarkList,
  getBookmarksFromLocalStorage,
  getCartOwnerFromLocalStorage,
  isLoginAsCartResetRequest,
  saveCartOwnerToLocalStorage,
  saveBookmarksToLocalStorage,
  removeCartFromLocalStorage,
  removeBookmarksFromLocalStorage,
  removeCartOwnerFromLocalStorage,
  removeCartItem,
  saveCartToLocalStorage,
  setCartItemQuantity,
  upsertCartItem,
} from '../util/cartHelpers';

const OFFER_ACCEPTED_QUERY_TRANSITIONS = [
  purchaseTransitions.TRANSITION_ACCEPT_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_AUTO_ACCEPT_OFFER,
];
const OFFER_QUERY_PAGE_SIZE = 50;
const OFFER_QUERY_MAX_PAGES = 5;

const toEntityUuid = idLike => {
  if (!idLike) {
    return null;
  }

  if (typeof idLike === 'string') {
    return idLike;
  }

  return idLike?.uuid || idLike?.id?.uuid || null;
};

const toISODate = value => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toDateMs = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const parseAcceptedOfferAmountInSubunits = tx => {
  const offerPriceMajor = Number(tx?.attributes?.protectedData?.offerParams?.offerPrice);
  return Number.isFinite(offerPriceMajor) && offerPriceMajor > 0
    ? Math.round(offerPriceMajor * 100)
    : null;
};

const getAcceptedOfferAt = tx => {
  const acceptedTransitionSet = new Set(OFFER_ACCEPTED_QUERY_TRANSITIONS);
  const transitions = Array.isArray(tx?.attributes?.transitions) ? tx.attributes.transitions : [];
  const acceptedAt = transitions
    .filter(t => acceptedTransitionSet.has(t?.transition))
    .reduce((latest, t) => {
      const latestMs = toDateMs(latest);
      const currentMs = toDateMs(t?.createdAt);
      return currentMs > latestMs ? t?.createdAt : latest;
    }, null);

  return toISODate(acceptedAt || tx?.attributes?.lastTransitionedAt || tx?.attributes?.createdAt);
};

const indexListingsById = included => {
  const entities = Array.isArray(included) ? included : [];
  return entities.reduce((acc, entity) => {
    if (entity?.type !== 'listing') {
      return acc;
    }

    const id = toEntityUuid(entity?.id);
    if (!id) {
      return acc;
    }

    acc[id] = entity;
    return acc;
  }, {});
};

const buildAcceptedOfferContextFromTransaction = ({ tx, listingsById = {}, currentUserId }) => {
  if (!tx?.id) {
    return null;
  }

  const canonicalStatus = getCanonicalStatus(tx);
  if (canonicalStatus !== CANONICAL_STATUSES.OFFER_ACCEPTED) {
    return null;
  }

  const processName = resolveLatestProcessName(tx?.attributes?.processName);
  if (processName !== PURCHASE_PROCESS_NAME) {
    return null;
  }

  const listingId = toEntityUuid(tx?.relationships?.listing?.data?.id || tx?.listing?.id);
  const customerId = toEntityUuid(tx?.relationships?.customer?.data?.id || tx?.customer?.id);
  const providerId = toEntityUuid(tx?.relationships?.provider?.data?.id || tx?.provider?.id);
  const amount = parseAcceptedOfferAmountInSubunits(tx);
  const listingCurrency = listingsById?.[listingId]?.attributes?.price?.currency;
  const txCurrency = tx?.attributes?.payinTotal?.currency;
  const currency = (listingCurrency || txCurrency || '').toUpperCase();
  const acceptedAt = getAcceptedOfferAt(tx);

  if (!listingId || !customerId || !providerId || !amount || !currency || !acceptedAt) {
    return null;
  }

  if (currentUserId && customerId !== currentUserId) {
    return null;
  }

  return {
    listingId,
    acceptedOfferId: toEntityUuid(tx.id),
    offerPrice: { amount, currency },
    offerCurrency: currency,
    offerAcceptedAt: acceptedAt,
    offerBuyerId: customerId,
    offerSellerId: providerId,
    offerListingId: listingId,
  };
};

const fetchAcceptedOfferContextsForListings = async ({
  sdk,
  listingIds = [],
  currentUserId = null,
}) => {
  if (!sdk?.transactions?.query || !Array.isArray(listingIds) || listingIds.length === 0) {
    return {};
  }

  const targetListingIds = new Set(listingIds.filter(Boolean));
  const contextsByListingId = {};
  let page = 1;
  let totalPages = 1;

  do {
    const response = await sdk.transactions.query({
      lastTransitions: OFFER_ACCEPTED_QUERY_TRANSITIONS,
      include: ['listing', 'customer', 'provider'],
      'fields.transaction': [
        'createdAt',
        'lastTransition',
        'lastTransitionedAt',
        'state',
        'processName',
        'transitions',
        'protectedData',
        'payinTotal',
      ],
      'fields.listing': ['price'],
      per_page: OFFER_QUERY_PAGE_SIZE,
      page,
    });

    const txs = response?.data?.data || [];
    const listingsById = indexListingsById(response?.data?.included || []);
    txs.forEach(tx => {
      const context = buildAcceptedOfferContextFromTransaction({
        tx,
        listingsById,
        currentUserId,
      });
      if (!context || !targetListingIds.has(context.listingId)) {
        return;
      }

      const existing = contextsByListingId[context.listingId];
      const existingAcceptedAtMs = toDateMs(existing?.offerAcceptedAt);
      const nextAcceptedAtMs = toDateMs(context.offerAcceptedAt);
      if (!existing || nextAcceptedAtMs > existingAcceptedAtMs) {
        contextsByListingId[context.listingId] = context;
      }
    });

    totalPages = response?.data?.meta?.totalPages || 1;
    page += 1;
  } while (page <= totalPages && page <= OFFER_QUERY_MAX_PAGES);

  return contextsByListingId;
};

const mergeOfferContextsIntoCart = (cartItems, offerContextsByListingId) => {
  const normalized = normalizeCartItemsMap(cartItems || {});
  const next = { ...normalized };

  Object.keys(offerContextsByListingId || {}).forEach(listingId => {
    const context = offerContextsByListingId[listingId];
    const currentItem = next?.[listingId];
    if (!context || !currentItem) {
      return;
    }

    const hasExistingAcceptedOffer = Boolean(currentItem?.acceptedOfferId);
    const existingAcceptedAtMs = toDateMs(currentItem?.offerAcceptedAt);
    const nextAcceptedAtMs = toDateMs(context?.offerAcceptedAt);
    const shouldReplaceExisting =
      !hasExistingAcceptedOffer || (nextAcceptedAtMs && nextAcceptedAtMs >= existingAcceptedAtMs);

    if (!shouldReplaceExisting) {
      return;
    }

    next[listingId] = {
      ...currentItem,
      acceptedOfferId: context.acceptedOfferId,
      offerPrice: context.offerPrice,
      offerCurrency: context.offerCurrency,
      offerAcceptedAt: context.offerAcceptedAt,
      offerBuyerId: context.offerBuyerId,
      offerSellerId: context.offerSellerId,
      offerListingId: context.offerListingId,
    };
  });

  return normalizeCartItemsMap(next);
};

const createInitialState = () => {
  const initialItems = getCartFromLocalStorage();
  return {
    items: initialItems,
    itemCount: getCartItemCount(initialItems),
  };
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: createInitialState,
  reducers: {
    setCartItems: (state, action) => {
      state.items = normalizeCartItemsMap(action.payload || {});
      state.itemCount = getCartItemCount(state.items);
    },
    addItem: (state, action) => {
      const {
        listingId,
        quantity = 1,
        maxQuantity = null,
        sellerId = null,
        selectedSize = null,
        selectedSizeLabel = null,
        acceptedOfferId = null,
        offerPrice = null,
        offerCurrency = null,
        offerAcceptedAt = null,
        offerBuyerId = null,
        offerSellerId = null,
        offerListingId = null,
      } = action.payload || {};
      state.items = upsertCartItem({
        cartItems: state.items,
        listingId,
        quantity,
        maxQuantity,
        sellerId,
        selectedSize,
        selectedSizeLabel,
        acceptedOfferId,
        offerPrice,
        offerCurrency,
        offerAcceptedAt,
        offerBuyerId,
        offerSellerId,
        offerListingId,
      });
      state.itemCount = getCartItemCount(state.items);
    },
    removeItem: (state, action) => {
      const listingId = action.payload;
      state.items = removeCartItem({ cartItems: state.items, listingId });
      state.itemCount = getCartItemCount(state.items);
    },
    updateQuantity: (state, action) => {
      const { listingId, quantity, maxQuantity = null } = action.payload || {};
      state.items = setCartItemQuantity({
        cartItems: state.items,
        listingId,
        quantity,
        maxQuantity,
      });
      state.itemCount = getCartItemCount(state.items);
    },
    clearAll: state => {
      state.items = {};
      state.itemCount = 0;
    },
  },
});

const { setCartItems, addItem, removeItem, updateQuantity, clearAll } = cartSlice.actions;

export default cartSlice.reducer;

const uniqueCount = list => (Array.isArray(list) ? new Set(list.filter(Boolean)).size : 0);

const selectCurrentUserBookmarks = state =>
  state?.user?.currentUser?.attributes?.profile?.publicData?.bookmarks;
const selectIsAuthenticated = state => state?.auth?.isAuthenticated;
const selectLocalBookmarks = () => getBookmarksFromLocalStorage();

export const cartSelector = state => {
  const items = state?.cart?.items || {};
  return {
    items,
    itemCount: state?.cart?.itemCount || 0,
    itemsArray: cartItemsArray(items),
  };
};

export const selectCartItems = state => state?.cart?.items || {};
export const selectCartItemsArray = state => cartItemsArray(selectCartItems(state));
export const selectCartItemCount = state => state?.cart?.itemCount || 0;
export const selectCartCount = createSelector(
  selectCartItems,
  items => getCartItemCount(items)
);
export const selectIsInCart = (state, listingId) => Boolean(selectCartItems(state)?.[listingId]);
export const selectBookmarkCartCount = createSelector(
  selectIsAuthenticated,
  selectCurrentUserBookmarks,
  selectLocalBookmarks,
  (isAuthenticated, userBookmarks, localBookmarks) => {
    const source = isAuthenticated ? userBookmarks : localBookmarks;
    return uniqueCount(source);
  }
);

let cartHydrationInProgress = false;
let lastHydratedUserId = null;

const saveCartBrowserState = (cartItems, ownerId = null) => {
  const bookmarkIds = cartMapToBookmarkList(cartItems);
  saveCartToLocalStorage(cartItems);
  saveBookmarksToLocalStorage(bookmarkIds);

  if (ownerId) {
    saveCartOwnerToLocalStorage(ownerId);
  } else {
    removeCartOwnerFromLocalStorage();
  }
};

const clearCartBrowserState = () => {
  removeCartFromLocalStorage();
  removeBookmarksFromLocalStorage();
  removeCartOwnerFromLocalStorage();
};

const shouldSkipLocalCartMerge = ({
  currentUserId,
  isAuthenticated,
  isLoggedInAs,
  localCartOwner,
} = {}) => {
  if (!isAuthenticated || !currentUserId) {
    return false;
  }

  const isSwitchingAuthenticatedUsers =
    lastHydratedUserId && lastHydratedUserId !== currentUserId;

  return Boolean(
    isLoggedInAs ||
      isLoginAsCartResetRequest() ||
      isSwitchingAuthenticatedUsers ||
      (localCartOwner && localCartOwner !== currentUserId)
  );
};

const persistAuthenticatedCart = async (dispatch, getState, nextItems) => {
  const isAuthenticated = getState()?.auth?.isAuthenticated;
  if (!isAuthenticated) {
    return;
  }

  const bookmarkIds = cartMapToBookmarkList(nextItems);
  const resultAction = await dispatch(
    updateProfile({
      publicData: {
        cart: nextItems || {},
        bookmarks: bookmarkIds,
      },
    })
  );

  if (resultAction?.type === updateProfileThunk.rejected.type) {
    throw resultAction.payload || new Error('Could not persist cart');
  }
};

export const hydrateCart = () => async (dispatch, getState, sdk) => {
  if (cartHydrationInProgress) {
    return;
  }
  cartHydrationInProgress = true;

  const state = getState();
  const currentUser = state?.user?.currentUser;
  const currentUserId = toEntityUuid(currentUser?.id);
  const isAuthenticated = Boolean(state?.auth?.isAuthenticated);
  const isLoggedInAs = Boolean(state?.auth?.isLoggedInAs);
  const localCartOwner = getCartOwnerFromLocalStorage();
  const skipLocalCartMerge = shouldSkipLocalCartMerge({
    currentUserId,
    isAuthenticated,
    isLoggedInAs,
    localCartOwner,
  });
  const includeOwnedLocalCart = Boolean(
    isAuthenticated && currentUserId && localCartOwner === currentUserId && !skipLocalCartMerge
  );
  const localCart = skipLocalCartMerge
    ? {}
    : getCartFromLocalStorage({ includeOwned: includeOwnedLocalCart });
  const localBookmarks = skipLocalCartMerge
    ? []
    : getBookmarksFromLocalStorage({ includeOwned: includeOwnedLocalCart });
  const userCartMaybe = currentUser?.attributes?.profile?.publicData?.cart;
  const userBookmarksMaybe = currentUser?.attributes?.profile?.publicData?.bookmarks;
  const merged = normalizeCartItemsMap(
    mergeCartMaps(
      localCart,
      bookmarksArrayToCartMap(localBookmarks),
      userCartMaybe,
      bookmarksArrayToCartMap(userBookmarksMaybe)
    )
  );

  let nextItems = merged;
  if (isAuthenticated && currentUserId) {
    const listingIdsWithoutOfferContext = Object.keys(merged).filter(
      listingId => !merged?.[listingId]?.acceptedOfferId
    );
    if (listingIdsWithoutOfferContext.length > 0) {
      try {
        const acceptedOfferContexts = await fetchAcceptedOfferContextsForListings({
          sdk,
          listingIds: listingIdsWithoutOfferContext,
          currentUserId,
        });
        nextItems = mergeOfferContextsIntoCart(merged, acceptedOfferContexts);
      } catch (_error) {
        nextItems = merged;
      }
    }
  }

  dispatch(setCartItems(nextItems));
  saveCartBrowserState(nextItems, isAuthenticated && currentUserId ? currentUserId : null);
  try {
    await persistAuthenticatedCart(dispatch, getState, nextItems);
  } catch (e) {
    // Keep cart UX resilient even if profile update fails.
  } finally {
    lastHydratedUserId = isAuthenticated && currentUserId ? currentUserId : null;
    cartHydrationInProgress = false;
  }

  return nextItems;
};

export const addItemToCart = ({
  listingId,
  quantity = 1,
  maxQuantity = null,
  sellerId = null,
  selectedSize = null,
  selectedSizeLabel = null,
  acceptedOfferId = null,
  offerPrice = null,
  offerCurrency = null,
  offerAcceptedAt = null,
  offerBuyerId = null,
  offerSellerId = null,
  offerListingId = null,
} = {}) => async (dispatch, getState, sdk) => {
  if (!listingId) {
    return getCartFromLocalStorage();
  }

  if (Number.isFinite(maxQuantity) && maxQuantity <= 0) {
    return selectCartItems(getState());
  }

  const state = getState();
  const currentUserId = toEntityUuid(state?.user?.currentUser?.id);
  const isAuthenticated = Boolean(state?.auth?.isAuthenticated);
  const hasExplicitOfferContext = Boolean(acceptedOfferId && offerPrice && offerCurrency);
  let acceptedOfferContext = {};

  if (hasExplicitOfferContext) {
    acceptedOfferContext = {
      acceptedOfferId,
      offerPrice,
      offerCurrency,
      ...(offerAcceptedAt ? { offerAcceptedAt } : {}),
      ...(offerBuyerId ? { offerBuyerId } : {}),
      ...(offerSellerId ? { offerSellerId } : {}),
      ...(offerListingId ? { offerListingId } : {}),
    };
  } else if (isAuthenticated && currentUserId) {
    try {
      const acceptedOfferContexts = await fetchAcceptedOfferContextsForListings({
        sdk,
        listingIds: [listingId],
        currentUserId,
      });
      acceptedOfferContext = acceptedOfferContexts?.[listingId] || {};
    } catch (_error) {
      acceptedOfferContext = {};
    }
  }

  dispatch(
    addItem({
      listingId,
      quantity,
      maxQuantity,
      sellerId,
      selectedSize,
      selectedSizeLabel,
      ...acceptedOfferContext,
    })
  );
  const nextItems = selectCartItems(getState());
  saveCartBrowserState(nextItems, isAuthenticated && currentUserId ? currentUserId : null);
  if (!isAuthenticated) {
    lastHydratedUserId = null;
  }

  // Fire a Klaviyo Added to Cart event. This thunk is the single user-initiated
  // add path (cart hydration uses setCartItems, so we don't misfire on restore).
  // The listing is resolved from the marketplaceData store — it's there because
  // the user added from a card or the listing page, both of which fetch it.
  // trackAddedToCart no-ops if the listing is missing or Klaviyo isn't configured.
  try {
    const listing = getListingsById(getState(), [new UUID(listingId)])[0];
    if (listing) {
      trackAddedToCart(listing, { quantity });
    }
  } catch (e) {
    // Never let analytics break the cart.
  }

  try {
    await persistAuthenticatedCart(dispatch, getState, nextItems);
  } catch (e) {
    // Keep cart UX resilient even if profile update fails.
  }

  return nextItems;
};

export const removeItemFromCart = ({ listingId } = {}) => async (dispatch, getState) => {
  dispatch(removeItem(listingId));
  const state = getState();
  const currentUserId = toEntityUuid(state?.user?.currentUser?.id);
  const isAuthenticated = Boolean(state?.auth?.isAuthenticated);
  const nextItems = selectCartItems(getState());
  saveCartBrowserState(nextItems, isAuthenticated && currentUserId ? currentUserId : null);
  if (!isAuthenticated) {
    lastHydratedUserId = null;
  }

  try {
    await persistAuthenticatedCart(dispatch, getState, nextItems);
  } catch (e) {
    // Keep cart UX resilient even if profile update fails.
  }

  return nextItems;
};

export const removeItemsFromCart = ({ listingIds = [] } = {}) => async (dispatch, getState) => {
  const uniqueListingIds = Array.from(new Set((listingIds || []).filter(Boolean)));
  if (!uniqueListingIds.length) {
    return selectCartItems(getState());
  }

  const nextItems = uniqueListingIds.reduce((acc, listingId) => {
    return removeCartItem({ cartItems: acc, listingId });
  }, selectCartItems(getState()));

  dispatch(setCartItems(nextItems));
  const state = getState();
  const currentUserId = toEntityUuid(state?.user?.currentUser?.id);
  const isAuthenticated = Boolean(state?.auth?.isAuthenticated);
  saveCartBrowserState(nextItems, isAuthenticated && currentUserId ? currentUserId : null);
  if (!isAuthenticated) {
    lastHydratedUserId = null;
  }

  try {
    await persistAuthenticatedCart(dispatch, getState, nextItems);
  } catch (e) {
    // Keep cart UX resilient even if profile update fails.
  }

  return nextItems;
};

export const updateItemQuantityInCart = ({
  listingId,
  quantity,
  maxQuantity = null,
} = {}) => async (dispatch, getState) => {
  dispatch(updateQuantity({ listingId, quantity, maxQuantity }));
  const state = getState();
  const currentUserId = toEntityUuid(state?.user?.currentUser?.id);
  const isAuthenticated = Boolean(state?.auth?.isAuthenticated);
  const nextItems = selectCartItems(getState());
  saveCartBrowserState(nextItems, isAuthenticated && currentUserId ? currentUserId : null);
  if (!isAuthenticated) {
    lastHydratedUserId = null;
  }

  try {
    await persistAuthenticatedCart(dispatch, getState, nextItems);
  } catch (e) {
    // Keep cart UX resilient even if profile update fails.
  }

  return nextItems;
};

export const clearCart = () => async (dispatch, getState) => {
  dispatch(clearAll());
  clearCartBrowserState();

  try {
    await persistAuthenticatedCart(dispatch, getState, {});
  } catch (e) {
    // Keep cart UX resilient even if profile update fails.
  }
};

export const __testables = {
  resetCartHydrationState: () => {
    cartHydrationInProgress = false;
    lastHydratedUserId = null;
  },
};
